import BreadCrumb from "Common/BreadCrumb";
import TransactionTable from "Common/Tables/TransactionTable";
import { useEffect, useState } from "react";
import { debounce } from "lodash";
import { Container, Card, Button, Row, Col, Form } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import { authServices } from "Utils/auth/services";
import { currentDateTime, getStartTimeOfDay } from "Utils/datefilter";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const TransactionList = () => {
  const currentDate = new Date();
  const sevenDaysAgo = new Date(currentDate.setDate(currentDate.getDate() - 7));

  const [startDate, setStartDate] = useState<string | Date>(
    getStartTimeOfDay(sevenDaysAgo)
  );
  const [endDate, setEndDate] = useState<string | Date>(currentDateTime());
  const [limit, setLimit] = useState<number>(50);
  const [page, setPage] = useState<number>(1);
  const [listTotal, setListTotal] = useState<ProfitLossTotal>({
    total: 0,
    page: 0,
  });
  const [total, setTotal] = useState<number>(0);
  const [totalWithdraw, setTotalWithdraw] = useState<number>(0);
  const [transactionData, setTransactionData] = useState<TransactionData[]>([]);
  const [userName, setUserName] = useState<string>("");
  const [parentName, setParentName] = useState<string>("");
  const [method, setMethod] = useState<string>("ALL");
  const [type, setType] = useState<string>("ALL");

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserName(event.target.value);
  };

  const handleParentNameeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setParentName(event.target.value);
  };

  const handleMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const method = event.target.value;
    setMethod(method);
    setPage(1);

    getAllTransaction({
      statement_type: method,
      page: 1,
    });
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const type = event.target.value;
    setType(type);
    setPage(1);

    getAllTransaction({
      status: type,
      page: 1,
    });
  };

  const fetchUserNameOptions = debounce(async (input: string) => {
    if (input.length >= 3) {
      setPage(1);

      getAllTransaction({
        page: 1,
        search: {
          user_name: input,
          ...(parentName && { parent_user_name: parentName }),
        },
      });
    }
  }, 300);

  useEffect(() => {
    fetchUserNameOptions(userName);
    // eslint-disable-next-line
  }, [userName]);

  const fetchParentNameOptions = debounce(async (input: string) => {
    if (input.length >= 3) {
      setPage(1);

      getAllTransaction({
        page: 1,
        search: {
            ...(userName && { user_name: userName }),
            parent_user_name: input,
        },
      });
    }
  }, 300);

  useEffect(() => {
    fetchParentNameOptions(parentName);
    // eslint-disable-next-line
  }, [parentName]);

  const getAllTransaction = async (filters: any = {}) => {
    const { response } = await authServices.getAllRequest({
      from_date: startDate,
      to_date: endDate,
      statement_type: method,
      status: type,
      search: {
        ...(userName && { user_name: userName }),
        ...(parentName && { parent_user_name: parentName }),
      },
      limit: limit,
      page: page,
      ...filters,
    });
    if (response?.status && response?.data) {
      setTransactionData(response?.data?.data);
      setListTotal({
        total: response?.data?.total / response?.data?.limit || 0,
        page: response?.data?.page || 1,
      });
      setTotal(response?.data?.totalDeposit);
      setTotalWithdraw(response?.data?.totalWithdrawal);
    } else {
      setTransactionData([]);
      setListTotal({
        total: 0,
        page: 1,
      });
      setTotal(0);
      setTotalWithdraw(0);
    }
  };

  useEffect(() => {
    getAllTransaction({});
    // eslint-disable-next-line
  }, []);

  const filterSubmit = () => {
    setPage(1);

    getAllTransaction({
      page: 1,
    });
  };

  const clearFilter = () => {
    setUserName("");
    setParentName("");
    setType("ALL");
    setMethod("ALL");
    setStartDate(getStartTimeOfDay(sevenDaysAgo));
    setEndDate(currentDateTime());
    setPage(1);

    getAllTransaction({
      from_date: getStartTimeOfDay(sevenDaysAgo),
      to_date: currentDateTime(),
      search: {},
      page: 1,
      statement_type: "ALL",
      status: "ALL",
    });
  };

  const handlePageFilter = (page: number, limit: number) => {
    setPage(page);
    setLimit(limit);

    getAllTransaction({
      page: page,
      limit: limit,
    });
  };

  function formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    return new Intl.DateTimeFormat("en-US", options).format(date);
  }

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text("D/W Report", 10, 10);

    const userDataToExport = transactionData?.map((user, index) => [
        index + 1,
        user.user_name,
        user.statement_type,
        user.parent_user_name,
        user.amount = user.statement_type === 'WITHDRAW_REQUEST' ? `-${user.amount}` : user.amount,
        user.created_at = formatDateTime( user.created_at),
        user.updatedAt = formatDateTime( user.updatedAt),
        user.remark,
        user.status
    ]);

    const headers = ["No", "User Name", "Type", "Parent Name", "Amount", "Request Time", "Updated Time", "Remark", "Status"];

    autoTable(doc, {
        head: [headers],
        body: userDataToExport as any,
    });
    doc.save("d/w_report.pdf");
};

const downloadXML = () => {

    let xmlContent = `<?xml version="1.0" encoding="UTF-8"?>\n<DWReport>\n`;

    transactionData?.forEach((user, id) => {
        xmlContent += `  <DWReport>\n`;
        xmlContent += `    <No>${id + 1}</No>\n`;
        xmlContent += `    <UserName>${user.user_name}</UserName>\n`;
        xmlContent += `    <Type>${user.statement_type}</Type>\n`;
        xmlContent += `    <ParentName>${user.parent_user_name}</ParentName>\n`;
        xmlContent += `    <Amount>${user.statement_type === 'WITHDRAW_REQUEST' ? `-${user.amount}` : user.amount}</Amount>\n`;
        xmlContent += `    <RequestTime>${formatDateTime( user.created_at)}</RequestTime>\n`;
        xmlContent += `    <UpdatedTime>${formatDateTime( user.updatedAt)}</UpdatedTime>\n`;
        xmlContent += `    <Remark>${user.remark}</Remark>\n`;
        xmlContent += `    <Status>${user.status}</Status>\n`;
        xmlContent += `  </DWReport>\n`;
    });

    xmlContent += `</DWReport>`;

    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'd/w_report.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

  return (
    <div className="page-content">
      <Container fluid>
        <div className="position-relative mb-3 d-flex justify-content-between align-items-center">
          <BreadCrumb title="D/W Report" pageTitle="Dashboard" back />
          <h5 className="mt-4 text-end">
          <span className="text-muted">Total Deposit : {total} Total Withdraw : {totalWithdraw}</span>
        </h5>
        </div>
        
        <Card className="mt-3">
          <Card.Header className="border-bottom pb-3">
            <Row className="align-items-end g-3">
              <Col lg={3}>
                <Form.Label>Start Date</Form.Label>
                <Flatpickr
                  className="form-control"
                  options={{
                    enableTime: true,
                    dateFormat: "Y-m-d H:i:s",
                    defaultDate: [startDate],
                    maxDate: endDate,
                  }}
                  value={startDate}
                  onChange={([date]: [Date]) => {
                    setStartDate(new Date(date));
                  }}
                />
              </Col>
              <Col lg={3}>
                <Form.Label>End Date</Form.Label>
                <Flatpickr
                  className="form-control"
                  options={{
                    enableTime: true,
                    dateFormat: "Y-m-d H:i:s",
                    defaultDate: [endDate],
                    maxDate: currentDateTime(),
                  }}
                  value={endDate}
                  onChange={([date]: [Date]) => {
                    setEndDate(new Date(date));
                  }}
                />
              </Col>
              <Col lg={3}>
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  value={userName}
                  placeholder="Enter Username"
                  onChange={handleUsernameChange}
                />
              </Col>
              <Col lg={3}>
                <Form.Label>Parent Username</Form.Label>
                <Form.Control
                  type="text"
                  value={parentName}
                  placeholder="Enter Parent Username"
                  onChange={handleParentNameeChange}
                />
              </Col>
              <Col lg={3}>
                <Form.Label>Status</Form.Label>
                <div className="d-flex">
                  <Form.Select
                    className="me-1"
                    value={type}
                    onChange={handleTypeChange}
                  >
                    <option value="ALL">All</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="PENDING">Pending</option>
                    <option value="PROGRESS">Progress</option>
                  </Form.Select>
                </div>
              </Col>
              <Col lg={3}>
                <Form.Label>Status</Form.Label>
                <div className="d-flex">
                  <Form.Select
                    className="me-1"
                    value={method}
                    onChange={handleMethodChange}
                  >
                    <option value="ALL">All</option>
                    <option value="DEPOSIT_REQUEST">Deposit</option>
                    <option value="WITHDRAW_REQUEST">Withdraw</option>
                  </Form.Select>
                </div>
              </Col>
              <Col lg={4}>
                <Button className="me-2" onClick={filterSubmit}>
                  Search
                </Button>
                <Button variant="subtle-primary" onClick={clearFilter}>
                  Clear
                </Button>
                <Button variant="success" className="ms-2" onClick={downloadPDF}>
                  <i className="fs-md bi bi-file-earmark-pdf-fill"></i>
                </Button>
                <Button variant="danger" className="ms-2" onClick={downloadXML}>
                  <i className="fs-md bi bi-file-earmark-excel-fill"></i>
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <TransactionTable
              transactionData={transactionData}
              page={page}
              limit={limit}
              listTotal={listTotal}
              handlePageFilter={handlePageFilter}
            />
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default TransactionList;
